import React, { useState } from 'react';

import { cmToInches, sanitizeNumber } from './calculatorUtils';
import css from './calculatorStyles.module.css';

/**
 * Yoke rules (from Tom Parker & Rob Kendall seminar):
 *
 * Women: O (<47 kg), A (47-55), B (55-65), C (>65)
 * Men: D (height >188 cm OR chest >112 cm), B (<60 kg), C (otherwise)
 */
function getYoke(sex, weightKg, heightCm, chestCm) {
  if (sex === 'women') {
    if (weightKg < 47) return { yoke: 'O', reason: 'Women weighing less than 47 kg.' };
    if (weightKg <= 55) return { yoke: 'A', reason: 'Women weighing 47-55 kg.' };
    if (weightKg <= 65) return { yoke: 'B', reason: 'Women weighing 55-65 kg.' };
    return { yoke: 'C', reason: 'Women weighing more than 65 kg.' };
  }

  if (heightCm > 188 || chestCm > 112) {
    const reason =
      heightCm > 188
        ? 'Men taller than 188 cm.'
        : 'Men with chest circumference greater than 112 cm.';
    return { yoke: 'D', reason };
  }
  if (weightKg < 60) return { yoke: 'B', reason: 'Men weighing less than 60 kg.' };
  return { yoke: 'C', reason: 'Men weighing 60+ kg, under 188 cm tall, chest 112 cm or less.' };
}

function computeMLW(heightCm, inseamCm) {
  const heightIn = cmToInches(heightCm);
  const inseamIn = cmToInches(inseamCm);
  const mlwRaw = heightIn - inseamIn - 20;
  const mlw = Math.round(mlwRaw);
  return {
    mlw,
    detail: `Height ${heightCm} cm (${heightIn.toFixed(1)} in) - Inseam ${inseamCm} cm (${inseamIn.toFixed(1)} in) - 20`,
  };
}

// MLW below yoke-specific minimum is a warning
const YOKE_MIN_MLW = { O: 13, A: 14, B: 15, C: 16, D: 17 };

const INITIAL_STATE = {
  sex: '',
  height: '',
  weight: '',
  chest: '',
  inseam: '',
};

const SunPathCalculator = () => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm(INITIAL_STATE);
    setResults(null);
    setError(null);
  };

  const handleCalculate = () => {
    setResults(null);
    setError(null);

    if (!form.sex || !form.height || !form.weight || !form.chest || !form.inseam) {
      setError('Please complete all fields.');
      return;
    }

    const heightCm = sanitizeNumber(form.height);
    const weightKg = sanitizeNumber(form.weight);
    const chestCm = sanitizeNumber(form.chest);
    const inseamCm = sanitizeNumber(form.inseam);

    if (isNaN(heightCm) || isNaN(weightKg) || isNaN(chestCm) || isNaN(inseamCm)) {
      setError('Please enter valid numbers for all measurements.');
      return;
    }
    if (heightCm < 100 || heightCm > 230) {
      setError('Height must be between 100 and 230 cm.');
      return;
    }
    if (weightKg < 30 || weightKg > 160) {
      setError('Weight must be between 30 and 160 kg.');
      return;
    }
    if (chestCm < 70 || chestCm > 140) {
      setError('Chest circumference must be between 70 and 140 cm.');
      return;
    }
    if (inseamCm < 50 || inseamCm > 95) {
      setError('Inseam must be between 50 and 95 cm.');
      return;
    }
    if (inseamCm >= heightCm) {
      setError('Inseam should be less than height.');
      return;
    }

    const { yoke, reason } = getYoke(form.sex, weightKg, heightCm, chestCm);
    const { mlw, detail } = computeMLW(heightCm, inseamCm);

    setResults({ yoke, yokeReason: reason, mlw, mlwDetail: detail });

    const minMlw = YOKE_MIN_MLW[yoke];
    if (minMlw != null && mlw < minMlw) {
      setError(
        'The calculated MLW is below the minimum for the recommended yoke. Please contact Sun Path or talk to your rigger.'
      );
    }
  };

  return (
    <div className={css.calculator}>
      <section className={css.card}>
        <h4 className={css.sectionTitle}>Your Measurements</h4>
        <div className={css.grid}>
          <div className={css.field}>
            <label className={css.label} htmlFor="sp-sex">Sex</label>
            <select
              id="sp-sex"
              className={css.select}
              name="sex"
              value={form.sex}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              <option value="women">Women</option>
              <option value="men">Men</option>
            </select>
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="sp-height">Height (cm)</label>
            <input
              id="sp-height"
              className={css.input}
              name="height"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="100"
              max="230"
              placeholder="e.g., 178"
              value={form.height}
              onChange={handleChange}
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="sp-weight">Weight (kg)</label>
            <input
              id="sp-weight"
              className={css.input}
              name="weight"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="30"
              max="160"
              placeholder="e.g., 75"
              value={form.weight}
              onChange={handleChange}
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="sp-chest">Chest Circumference (cm)</label>
            <input
              id="sp-chest"
              className={css.input}
              name="chest"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="70"
              max="140"
              placeholder="e.g., 102"
              value={form.chest}
              onChange={handleChange}
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="sp-inseam">Inseam (cm)</label>
            <input
              id="sp-inseam"
              className={css.input}
              name="inseam"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="50"
              max="95"
              placeholder="e.g., 82"
              value={form.inseam}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className={css.actions}>
          <button type="button" className={css.btnPrimary} onClick={handleCalculate}>
            Calculate
          </button>
          <button type="button" className={css.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>

        {results && (
          <div className={css.results}>
            <div className={css.tile}>
              <h4 className={css.tileTitle}>Recommended Yoke</h4>
              <div className={css.tileRight}>
                <div className={css.tileValue}>{results.yoke}</div>
              </div>
            </div>
            <div className={css.tile}>
              <h4 className={css.tileTitle}>Recommended MLW</h4>
              <div className={css.tileRight}>
                <div className={css.tileValue}>{String(results.mlw)}</div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className={`${css.error} ${css.errorVisible}`} role="alert">
            {error}
          </div>
        )}
      </section>
    </div>
  );
};

export default SunPathCalculator;
